import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { Camera, Mic, Check, AlertCircle, Loader2, RotateCcw, Settings, UserCheck, ChevronDown, X, Shield, Monitor, BookOpen, Hand } from './icons/AssessmentIcons';
import axios from 'axios';
import studentRequest from '../utils/studentRequest';
import { setSelectedMediaDevices } from '../utils/mediaDevices';
import { pickAudioMimeType } from '../utils/audioFormat';
import { COLORS } from '../theme';

// --- Styled Components ---

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 10px;
  background: #fff;
  color: ${COLORS.TEXT_PRIMARY};
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: ${COLORS.HEADING};
  margin-bottom: 4px;
`;

const Subtitle = styled.p`
  font-size: 13px;
  color: ${COLORS.TEXT_SECONDARY};
  margin-bottom: 16px;
  text-align: center;
  max-width: 500px;
`;

// Expectations panel shown on the verification page so candidates know what
// good proctoring conduct looks like before they start. Aptitude-only for now;
// gated by the assessmentType prop so non-aptitude flows are untouched.
const ExpectationsPanel = styled.div`
  width: 100%;
  max-width: 560px;
  margin: 0 0 20px;
  padding: 16px 18px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  text-align: left;
`;
const ExpectationHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.HEADING || '#111827'};
  margin-bottom: 12px;
`;
const ExpectationList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 10px;
  display: flex;
  flex-direction: column;
  gap: 9px;
`;
const ExpectationItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 12.5px;
  color: ${COLORS.TEXT_PRIMARY};
  line-height: 1.45;
  & > svg { flex-shrink: 0; margin-top: 1px; color: ${COLORS.PRIMARY || '#2563eb'}; }
`;
const ExpectationNote = styled.p`
  margin: 8px 0 0;
  font-size: 11.5px;
  color: ${COLORS.TEXT_SECONDARY};
`;

const VideoWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 640px;
  aspect-ratio: 16 / 9;
  background: #000;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  margin-bottom: 16px;
  border: 2px solid #fff;

  @media (max-width: 1024px), (orientation: portrait) {
    width: 85%;
    max-width: 350px;
    aspect-ratio: 9 / 16;
    height: auto;
    max-height: 60vh;
    margin: 0 auto 24px auto;
  }
`;

const VideoElement = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: ${props => props.mirror ? 'scaleX(-1)' : 'none'};

  @media (max-width: 1024px), (orientation: portrait) {
    object-fit: contain;
  }
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(0,0,0,0.4);
  color: #fff;
  padding: 20px;
  text-align: center;
  z-index: 10;
`;

const RecordButton = styled.button`
  background: ${COLORS.ERROR};
  color: white;
  border: none;
  padding: 0;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.4);
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    transform: scale(1.05);
    background: #ef4444;
  }
  
  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    transform: none;
  }

  &::after {
    content: '';
    position: absolute;
    top: -6px;
    left: -6px;
    right: -6px;
    bottom: -6px;
    border-radius: 50%;
    border: 2px solid ${COLORS.ERROR};
    opacity: 0.5;
    animation: ${props => props.recording ? 'pulse 1.5s infinite' : 'none'};
  }

  @keyframes pulse {
    0% { transform: scale(1); opacity: 0.5; }
    100% { transform: scale(1.2); opacity: 0; }
  }
`;

const ActionButton = styled.button`
  background: ${props => props.primary ? COLORS.PRIMARY : '#f3f4f6'};
  color: ${props => props.primary ? '#fff' : COLORS.TEXT_PRIMARY};
  border: 1px solid ${props => props.primary ? 'transparent' : '#e5e7eb'};
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.primary ? COLORS.BLUE_S_30 : '#e5e7eb'};
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
  &:disabled:hover {
    background: ${props => props.primary ? COLORS.PRIMARY : '#f3f4f6'};
  }
`;

const StatusPill = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 16px;
  background: ${props => {
        if (props.status === 'verified') return '#ecfdf5';
        if (props.status === 'failed') return '#fffbeb'; // Light orange background for failed
        return '#fff';
    }};
  color: ${props => {
        if (props.status === 'verified') return '#059669';
        if (props.status === 'failed') return '#F59E0B'; // Orange color for failed
        return '#64748b';
    }};
  border: 1px solid ${props => {
        if (props.status === 'verified') return '#d1fae5';
        if (props.status === 'failed') return '#fed7aa'; // Orange border for failed
        return '#e2e8f0';
    }};
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s ease;
`;

const ErrorBox = styled.div`
  background: #fffbeb;
  border: 1px solid #fed7aa;
  color: #d97706;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const VerificationStep = styled.div`
   width: 100%;
   max-width: 640px;
   display: flex;
   justify-content: center; /* Centered as per pills in screenshot */
   gap: 20px;
   margin-bottom: 20px;
   padding: 0 10px;
`;

// Settings Components
const SettingsButton = styled.button`
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 20;
    background: rgba(30, 41, 59, 0.8);
    color: white;
    border: none;
    border-radius: 6px;
    padding: 6px 12px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: background 0.2s;

    &:hover {
        background: rgba(30, 41, 59, 1);
    }
`;

const SettingsDropdown = styled.div`
    position: absolute;
    top: 45px;
    right: 10px;
    width: 280px;
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.15);
    padding: 16px;
    z-index: 30;
    border: 1px solid #e2e8f0;
`;

const Label = styled.label`
    display: block;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    color: #94a3b8;
    margin-bottom: 8px;
    margin-top: ${props => props.mt ? '16px' : '0'};
    letter-spacing: 0.5px;
`;

const Select = styled.select`
    width: 100%;
    padding: 8px 12px;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
    font-size: 13px;
    color: #334155;
    outline: none;
    background: #fff;
    cursor: pointer;

    &:focus {
        border-color: #4f46e5;
        box-shadow: 0 0 0 2px rgba(79, 70, 229, 0.1);
    }
`;

// --- Component ---

const TEST_PHRASES = [
    "I am ready to start my assessment.",
    "Microphone check, one two three.",
    "The quick brown fox jumps over the lazy dog."
];

const STRICT_MODE = true; // Set to false to allow bypassing checks

const BiometricCheck = ({ onVerificationComplete, onStartAssessment, studentId, isStarting = false, assessmentType = null }) => {
    const [stream, setStream] = useState(null);
    const [error, setError] = useState(null);
    const [isRecording, setIsRecording] = useState(false);
    const [recordedBlob, setRecordedBlob] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const [currentPhrase, setCurrentPhrase] = useState(TEST_PHRASES[0]);
    const [verificationStatus, setVerificationStatus] = useState('idle'); // idle, recording, verifying, verified, failed
    const [faceStatus, setFaceStatus] = useState('idle'); // idle, verified, failed
    const [audioStatus, setAudioStatus] = useState('idle'); // idle, verified, failed
    const [capturedImage, setCapturedImage] = useState(null);
    const capturedImageRef = useRef(null); // Ref to hold latest frame for closure access

    // Settings State
    const [showSettings, setShowSettings] = useState(false);
    const [videoDevices, setVideoDevices] = useState([]);
    const [audioDevices, setAudioDevices] = useState([]);
    const [selectedVideo, setSelectedVideo] = useState('');
    const [selectedAudio, setSelectedAudio] = useState('');

    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);
    // Dedicated audio-only recorder for the speech check. iOS Safari records a
    // SILENT audio track inside a combined video recording, so the backend
    // detect-audio check fails on iPhone; recording the mic on its own (a cloned
    // audio track) captures it reliably.
    const audioRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const audioBlobPromiseRef = useRef(null);

    // Enumerate Devices
    useEffect(() => {
        const getDevices = async () => {
            try {
                // Request permissions first to get labels
                await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
                const devices = await navigator.mediaDevices.enumerateDevices();

                const videos = devices.filter(device => device.kind === 'videoinput');
                const audios = devices.filter(device => device.kind === 'audioinput');

                setVideoDevices(videos);
                setAudioDevices(audios);

                if (videos.length > 0 && !selectedVideo) setSelectedVideo(videos[0].deviceId);
                if (audios.length > 0 && !selectedAudio) setSelectedAudio(audios[0].deviceId);
            } catch (err) {
                console.error("Error enumerating devices:", err);
            }
        };

        getDevices();
    }, []);

    // Persist the chosen camera/mic so the assessment screens reuse the SAME
    // devices instead of falling back to the browser default.
    useEffect(() => {
        setSelectedMediaDevices({ videoDeviceId: selectedVideo, audioDeviceId: selectedAudio });
    }, [selectedVideo, selectedAudio]);

    // Handle Video Preview
    useEffect(() => {
        if (videoRef.current) {
            if (recordedBlob) {
                videoRef.current.srcObject = null;
                videoRef.current.src = URL.createObjectURL(recordedBlob);
                videoRef.current.controls = true;
                videoRef.current.muted = false;
            } else if (streamRef.current) {
                videoRef.current.src = "";
                videoRef.current.srcObject = streamRef.current;
                videoRef.current.controls = false;
                videoRef.current.muted = !isPlaying;
            }
        }
    }, [recordedBlob, isPlaying]);

    // Initialize Camera when selection changes
    useEffect(() => {
        startCamera();
        return () => {
            stopCamera();
        };
    }, [selectedVideo, selectedAudio]);

    const startCamera = async () => {
        stopCamera(); // Stop ongoing stream first

        try {
            const constraints = {
                video: selectedVideo ? { deviceId: { exact: selectedVideo } } : true,
                audio: selectedAudio ? { deviceId: { exact: selectedAudio } } : true
            };

            const newStream = await navigator.mediaDevices.getUserMedia(constraints);
            setStream(newStream);
            streamRef.current = newStream;
            if (videoRef.current) {
                videoRef.current.srcObject = newStream;
            }
            setError(null);
        } catch (err) {
            console.error("Camera Error:", err);
            setError("Could not access camera/microphone. Please check permissions.");
        }
    };

    const stopCamera = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
        }
        setStream(null);
    };

    // Helper: Convert Blob to Base64. MediaRecorder for video+audio produces a
    // mime like `video/webm;codecs=vp8,opus` — that comma inside the codec
    // spec means `split(',')[1]` lands on "opus;base64" instead of the actual
    // base64 payload. Slice from the FIRST comma after `;base64` (the spec's
    // real separator) so multi-codec data URLs decode correctly.
    const blobToBase64 = (blob) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const result = reader.result || '';
                const marker = ';base64,';
                const idx = result.indexOf(marker);
                resolve(idx >= 0 ? result.slice(idx + marker.length) : '');
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    };

    // Helper: Capture Frame from Video
    const captureFrame = () => {
        if (!videoRef.current || videoRef.current.videoWidth === 0 || videoRef.current.videoHeight === 0) return null;
        try {
            const canvas = document.createElement('canvas');
            canvas.width = videoRef.current.videoWidth;
            canvas.height = videoRef.current.videoHeight;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
            return canvas.toDataURL('image/jpeg', 0.8).split(',')[1]; // Return base64
        } catch (e) {
            console.error("Frame capture error:", e);
            return null;
        }
    };

    const startRecording = () => {
        if (!streamRef.current) return;

        setRecordedBlob(null);
        setIsRecording(true);
        setVerificationStatus('recording');
        setFaceStatus('idle');
        setAudioStatus('idle');
        setCountdown(5);
        setCurrentPhrase(TEST_PHRASES[Math.floor(Math.random() * TEST_PHRASES.length)]);
        chunksRef.current = [];

        try {
            // Dedicated audio-only recorder (cloned mic track) so the speech
            // check gets real audio even on iOS Safari, where the combined
            // video recording captures a silent audio track. Best-effort: if it
            // fails we fall back to the combined video blob for detect-audio.
            audioChunksRef.current = [];
            audioRecorderRef.current = null;
            audioBlobPromiseRef.current = null;
            try {
                const audioTracks = streamRef.current.getAudioTracks();
                if (audioTracks.length) {
                    const clonedTrack = audioTracks[0].clone();
                    const audioOnlyStream = new MediaStream([clonedTrack]);
                    const aMime = pickAudioMimeType();
                    const aRec = new MediaRecorder(audioOnlyStream, aMime ? { mimeType: aMime } : undefined);
                    audioRecorderRef.current = aRec;
                    aRec.ondataavailable = (e) => {
                        if (e.data.size > 0) audioChunksRef.current.push(e.data);
                    };
                    audioBlobPromiseRef.current = new Promise((resolve) => {
                        aRec.onstop = () => {
                            try { clonedTrack.stop(); } catch (_) {}
                            const t = aRec.mimeType || 'audio/mp4';
                            resolve(audioChunksRef.current.length ? new Blob(audioChunksRef.current, { type: t }) : null);
                        };
                    });
                    aRec.start();
                }
            } catch (audioErr) {
                audioRecorderRef.current = null;
                audioBlobPromiseRef.current = null;
            }

            const recorder = new MediaRecorder(streamRef.current);
            mediaRecorderRef.current = recorder;

            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) chunksRef.current.push(e.data);
            };

            recorder.onstop = async () => {
                const videoBlob = new Blob(chunksRef.current, { type: recorder.mimeType || 'video/webm' });
                setRecordedBlob(videoBlob);
                setIsRecording(false);

                const freshFrame = captureFrame();
                const finalFrame = freshFrame || capturedImageRef.current;

                // Prefer the dedicated audio-only recording for the speech check
                // (reliable mic capture on iOS); fall back to the video blob.
                let audioBlob = null;
                try {
                    audioBlob = audioBlobPromiseRef.current ? await audioBlobPromiseRef.current : null;
                } catch (_) { audioBlob = null; }

                verifyIdentity(audioBlob || videoBlob, finalFrame, videoBlob);
            };

            recorder.start();

            // Capture initial frame as backup immediately
            const initialFrame = captureFrame();
            if (initialFrame) {
                setCapturedImage(initialFrame);
                capturedImageRef.current = initialFrame;
            }

            // Auto-stop after 5 seconds
            let timer = 5;
            const interval = setInterval(() => {
                timer--;
                setCountdown(timer);
                if (timer <= 0) {
                    clearInterval(interval);
                    stopRecording();
                }
            }, 1000);

            setTimeout(() => {
                const frame = captureFrame();
                if (frame) {
                    setCapturedImage(frame);
                    capturedImageRef.current = frame;
                }
            }, 2500);

        } catch (err) {
            setError("Failed to start recording.");
            setIsRecording(false);
        }
    };

    const stopRecording = () => {
        if (audioRecorderRef.current && audioRecorderRef.current.state === 'recording') {
            try { audioRecorderRef.current.stop(); } catch (_) {}
        }
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.stop();
        }
    };

    const resetRecording = () => {
        setRecordedBlob(null);
        setVerificationStatus('idle');
        setFaceStatus('idle');
        setAudioStatus('idle');
        setError(null);
        setCapturedImage(null);
        if (videoRef.current && streamRef.current) {
            videoRef.current.src = "";
            videoRef.current.srcObject = streamRef.current;
            videoRef.current.muted = true;
        }
    };

    const verifyIdentity = async (detectAudioBlob, finalImage, videoBlobForUpload) => {
        setVerificationStatus('verifying');
        setError(null);

        try {
            const audioBase64 = await blobToBase64(detectAudioBlob);
            const imageBase64 = finalImage || captureFrame();

            if (!imageBase64) throw new Error("Could not capture video frame.");

            // Start both checks in parallel
            const facePromise = axios.post(`${process.env.REACT_APP_FASTAPI_URL}/proctoring/verify-frame`, {
                student_id: studentId,
                frame_number: 1,
                image_data: imageBase64,
                min_confidence: 0.5
            }, { timeout: 60000 });

            const audioPromise = axios.post(`${process.env.REACT_APP_FASTAPI_URL}/proctoring/detect-audio`, {
                student_id: studentId,
                audio_data: audioBase64
            }, { timeout: 60000 });

            const [faceResult, audioResult] = await Promise.allSettled([facePromise, audioPromise]);

            // Process Face Result
            if (faceResult.status === 'rejected') {
                setFaceStatus('failed');
                throw faceResult.reason;
            }
            const faceResponse = faceResult.value;
            if (!faceResponse.data.success || !faceResponse.data.face_detected) {
                setFaceStatus('failed');
                throw new Error("Face validation failed. Ensure you are clearly visible.");
            }
            setFaceStatus('verified');

            // Process Audio Result
            if (audioResult.status === 'rejected') {
                setAudioStatus('failed');
                throw audioResult.reason;
            }
            const audioResponse = audioResult.value;
            if (!audioResponse.data.success || !audioResponse.data.audio_detected) {
                setAudioStatus('failed');
                throw new Error("Audio validation failed. Please speak clearly.");
            }
            setAudioStatus('verified');

            setVerificationStatus('verified');

            // Fire-and-forget: upload verification video AFTER checks pass.
            // Student is not blocked — they can click Start Assessment immediately.
            if (studentId && videoBlobForUpload) {
                const formData = new FormData();
                formData.append('studentId', studentId);
                formData.append('video', videoBlobForUpload, `verification_${studentId}.webm`);
                studentRequest.post(
                    'students/assessments/uploadVerificationVideo',
                    formData,
                    { headers: { 'Content-Type': 'multipart/form-data' } }
                ).then(r => {
                    console.log('[VERIFICATION_VIDEO] stored:', r?.data);
                }).catch(e => {
                    console.warn('[VERIFICATION_VIDEO] upload failed (non-blocking):', e?.message);
                });
            }
        } catch (err) {
            console.error("Verification Error:", err);
            const friendlyMessage = STRICT_MODE
                ? "Verification failed. Please ensure your camera and microphone are working and try again."
                : "Please preview your recording below. If your audio and video are working properly, you may start the assessment.";

            setError(friendlyMessage);
            setVerificationStatus('failed');

            if (err.message && err.message.includes("Face") && faceStatus !== 'failed') setFaceStatus('failed');
            else if (err.message && err.message.includes("Audio") && audioStatus !== 'failed') setAudioStatus('failed');
        }
    };

    // Determine Status Pill State
    const isVerified = verificationStatus === 'verified';

    return (
        <Container>
            <Title>Verification</Title>
            <Subtitle>
                Please record a short clip to ensure your device is set up properly.
            </Subtitle>

            {/* Proctoring guidelines moved to the assessment instruction page
                (ProctoringGuidelines component) — not shown on this verification
                screen anymore. */}

            <VerificationStep>
                <StatusPill status={faceStatus}>
                    {faceStatus === 'failed' ? <X size={16} /> : (faceStatus === 'verified' ? <Check size={16} /> : <UserCheck size={16} />)}
                    Face
                </StatusPill>
                <StatusPill status={audioStatus}>
                    {audioStatus === 'failed' ? <X size={16} /> : (audioStatus === 'verified' ? <Check size={16} /> : <Mic size={16} />)}
                    Speech
                </StatusPill>
            </VerificationStep>

            {error && (
                <ErrorBox>
                    <AlertCircle size={16} /> {error}
                </ErrorBox>
            )}



            <VideoWrapper>
                <SettingsButton onClick={() => setShowSettings(!showSettings)}>
                    <Settings size={14} /> Settings <ChevronDown size={12} />
                </SettingsButton>

                {showSettings && (
                    <SettingsDropdown>
                        <Label>CAMERA</Label>
                        <Select
                            value={selectedVideo}
                            onChange={(e) => {
                                setSelectedVideo(e.target.value);
                                setShowSettings(false);
                            }}
                        >
                            {videoDevices.map(device => (
                                <option key={device.deviceId} value={device.deviceId}>
                                    {device.label || `Camera ${device.deviceId.slice(0, 5)}...`}
                                </option>
                            ))}
                        </Select>

                        <Label mt>MICROPHONE</Label>
                        <Select
                            value={selectedAudio}
                            onChange={(e) => {
                                setSelectedAudio(e.target.value);
                                setShowSettings(false);
                            }}
                        >
                            {audioDevices.map(device => (
                                <option key={device.deviceId} value={device.deviceId}>
                                    {device.label || `Microphone ${device.deviceId.slice(0, 5)}...`}
                                </option>
                            ))}
                        </Select>
                    </SettingsDropdown>
                )}

                <VideoElement
                    ref={videoRef}
                    autoPlay
                    muted={!isPlaying}
                    mirror={false}
                    playsInline
                />

                {isRecording && (
                    <Overlay>
                        <h3 style={{ fontSize: 48, fontWeight: 'bold', color: '#ffffff', marginBottom: 0 }}>{countdown}</h3>
                        <p style={{ fontSize: 24, marginTop: 10, color: '#ffffff', textAlign: 'center', padding: '0 20px' }}>Say: "{currentPhrase}"</p>
                    </Overlay>
                )}
                {verificationStatus === 'idle' && (
                    <Overlay style={{ background: 'rgba(0,0,0,0.1)' }}>
                        <RecordButton onClick={startRecording}>
                            <div style={{ width: 24, height: 24, background: 'white', borderRadius: 4 }} />
                        </RecordButton>
                        <p style={{ marginTop: 12, fontSize: 14, fontWeight: 600, color: '#fff', textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>Tap to Record</p>
                    </Overlay>
                )}
            </VideoWrapper>

            {verificationStatus === 'recording' && (
                <p style={{ color: COLORS.ERROR, fontWeight: 600 }}>Recording...</p>
            )}

            {verificationStatus === 'failed' && (
                <div style={{ textAlign: 'center' }}>
                    <p style={{ marginBottom: 16, color: '#333' }}>
                        {error || "Verification couldn't complete. Please try again."}
                    </p>
                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                        {/* Verification is mandatory: a failed check loops the
                            candidate back to re-record. They cannot proceed to the
                            assessment until both face and speech checks pass. */}
                        <ActionButton primary onClick={resetRecording}>
                            <RotateCcw size={16} />
                            Try Again
                        </ActionButton>
                    </div>
                </div>
            )}

            {verificationStatus === 'verifying' && (
                <div style={{ textAlign: 'center', marginTop: 20 }}>
                    <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} />
                    <p style={{ marginTop: 10, color: COLORS.TEXT_SECONDARY }}>Verifying identity...</p>
                    <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                </div>
            )}

            {verificationStatus === 'verified' && (
                <div style={{ textAlign: 'center', marginTop: 16 }}>
                    <p style={{ marginBottom: 16, color: COLORS.SUCCESS, fontWeight: 600, fontSize: 16 }}>
                        <Check size={16} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} />
                        Verification Successful
                    </p>
                    <ActionButton primary onClick={onStartAssessment} disabled={isStarting} style={{ width: '100%', justifyContent: 'center', padding: '14px 20px', fontSize: 15, borderRadius: 10 }}>
                        {isStarting ? (
                            <>
                                <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                                Preparing…
                            </>
                        ) : 'Start Assessment'}
                    </ActionButton>
                    <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                </div>
            )}
        </Container>
    );
};

export default BiometricCheck;
